import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash, FaSearch, FaPlus } from 'react-icons/fa';
import FormGenerator, { FormFieldConfig } from '../FormGenerator/FormGenerator';
import { getFormFieldsFromColumns } from './DetailedListFormConfig';
import GenericFlyoutPanel from '../GenericFlyoutPanel/GenericFlyoutPanel';
import PopupDialog from '../PopupDialog/PopupDialog';
import './DetailedListFlyinPanel.scss';
import './DetailedList.scss';
import RoleGuard from '../RoleGuard';
import ColumnFilterPopup from './ColumnFilterPopup';
import Toolbar, { ToolbarButtonConfig, ToolbarFilterConfig } from '../Toolbar/Toolbar';

export interface DetailedListColumn<T> {
  key: string;
  header: string;
  dataField?: string; // allows binding to a specific JSON field
  filterValue?: (item: T) => string; // custom text extractor used for column filtering
  render?: (item: T) => React.ReactNode;
  width?: string | number;
  fixedWidth?: boolean; // If true, column width is fixed and not resizable
  align?: 'left' | 'center' | 'right'; 
  className?: string;
  type?: 'string' | 'number' | 'select' | 'checkbox' | 'multiline' | 'date';
  options?: { label: string; value: any }[]; // for select fields
  required?: boolean; // <-- allow required property for form integration
  defaultValue?: any; // <-- new: default value for add form
  multiline?: boolean; // for string fields, if true use multiline
  rows?: number; // for multiline
}

export interface DataSource<T> {
  data: T[];
  setData: (data: T[]) => void;
}

export interface DetailedListProps<T> {
  columns?: DetailedListColumn<T>[];
  items?: T[]; // deprecated, for backward compatibility
  dataSource?: DataSource<T>; // new preferred way
  rowKey: (item: T) => string | number;
  actions?: (item: T) => React.ReactNode;
  className?: string;
  editRole?: string;
  addRole?: string;
  deleteRole?: string;
  onChange?: (items: T[], action: 'add' | 'edit' | 'delete', changedItem: T) => void;
  formFields?: FormFieldConfig[]; // allow custom form fields
  actionsColumnWidth?: string | number; // width for actions column
  actionsColumnFixed?: boolean; // fixed width for actions column
  onEditRequest?: (item: T) => void; // override built-in edit flyout
  onAddRequest?: () => void; // override built-in add flyout
  onDeleteRequest?: (item: T) => void; // override built-in delete (local-only) with an API-backed handler
  disableAdd?: boolean; // completely hides the add button in the toolbar
  disableDelete?: boolean; // hides the delete button on every row
  disableEdit?: boolean; // disables double-click row editing and hides the edit button
  /** Extra buttons appended to the toolbar (after the built-in Add button). */
  toolbarButtons?: ToolbarButtonConfig[];
  /** Filter controls shown on the left side of the toolbar. */
  toolbarFilters?: ToolbarFilterConfig[];
}

function DetailedListComponent<T>({ columns, items, dataSource, rowKey, actions, className, onChange, formFields, editRole = "", addRole = "", deleteRole = "", actionsColumnWidth = 140, actionsColumnFixed = false, onEditRequest, onAddRequest, onDeleteRequest, disableAdd = false, disableDelete = false, disableEdit = false, toolbarButtons = [], toolbarFilters = [] }: DetailedListProps<T>) {
  const { t } = useTranslation('common');
  // Use dataSource if provided, otherwise fallback to internal state or items prop
  const [internalItems, setInternalItems] = useState<T[]>(items || []);

  // Keep internal state in sync with items prop if items changes and no dataSource
  useEffect(() => {
    if (!dataSource && items) setInternalItems(items);
  }, [items, dataSource]); 

  const getItems = () => dataSource ? dataSource.data : internalItems;
  const setItems = (newItems: T[]) => dataSource ? dataSource.setData(newItems) : setInternalItems(newItems);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const [colWidths, setColWidths] = useState<{ [key: string]: number | undefined }>({});
  const resizingCol = useRef<string | null>(null);
  const startX = useRef<number>(0);
  const startWidth = useRef<number>(0);
  const [editItem, setEditItem] = useState<T | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteItem, setDeleteItem] = useState<T | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [filterValues, setFilterValues] = useState<{ [key: string]: string }>({});
  const [filterAnchor, setFilterAnchor] = useState<{ key: string, ref: React.RefObject<HTMLSpanElement> } | null>(null);

  // If columns are not provided, infer from first item
  const inferredColumns: DetailedListColumn<T>[] = columns && columns.length > 0
    ? columns
    : getItems().length > 0
      ? Object.keys(getItems()[0] as any).filter(k => k !== 'id').map(key => ({ key, header: key.charAt(0).toUpperCase() + key.slice(1) }))
      : [];

  const handleSort = (col: DetailedListColumn<T>) => {
    if (sortKey !== col.key) {
      setSortKey(col.key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else if (sortDir === 'desc') {
      setSortKey(null);
      setSortDir(null);
    } else {
      setSortDir('asc');
    }
  };

  const handleResizeStart = (e: React.MouseEvent, colKey: string) => {
    resizingCol.current = colKey;
    startX.current = e.clientX;
    startWidth.current = colWidths[colKey] || (e.target as HTMLElement).parentElement?.offsetWidth || 120;
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!resizingCol.current) return;
    const delta = e.clientX - startX.current;
    setColWidths(w => ({ ...w, [resizingCol.current!]: Math.max(60, startWidth.current + delta) }));
  };

  const handleResizeEnd = () => {
    resizingCol.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  const formFieldsToUse: FormFieldConfig[] = formFields ?? getFormFieldsFromColumns(inferredColumns);

  // Helper to truncate text with ... only if it overflows the cell
  const truncate = (value: any, max = 40, cellWidth?: number) => {
    if (typeof value === 'string') {
      // If a custom width is set, estimate chars that fit (8px per char as a rough guess)
      const estMax = cellWidth ? Math.floor((cellWidth - 24) / 8) : max;
      if (value.length > estMax) {
        return value.slice(0, estMax) + '...';
      }
    }
    return value;
  };

  // Use getItems() everywhere instead of items/internalItems
  let dataSourceItems = getItems();
  // Filtering logic
  let filteredItems = getItems();
  Object.entries(filterValues).forEach(([key, value]) => {
    if (value) {
      filteredItems = filteredItems.filter(item => {
        const col = inferredColumns.find(c => c.key === key);
        if (!col) return true;
        let cellValue: string;
        if (col.filterValue) {
          cellValue = col.filterValue(item);
        } else if (col.dataField) {
          cellValue = String((item as any)[col.dataField] ?? '');
        } else {
          cellValue = String((item as any)[col.key] ?? '');
        }
        return cellValue.toLowerCase().includes(value.toLowerCase());
      });
    }
  });
  let sortedItems = filteredItems;
  if (sortKey && sortDir) {
    sortedItems = [...filteredItems].sort((a, b) => {
      const aValue = columns?.find(c => c.key === sortKey)?.dataField ? (a as any)[columns?.find(c => c.key === sortKey)?.dataField!] : (a as any)[sortKey];
      const bValue = columns?.find(c => c.key === sortKey)?.dataField ? (b as any)[columns?.find(c => c.key === sortKey)?.dataField!] : (b as any)[sortKey];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (aValue === bValue) return 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return sortDir === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }

  const handleEdit = (item: T) => {
    if (onEditRequest) { onEditRequest(item); return; }
    setEditItem(item);
    setShowEdit(true);
  };
  const handleEditClose = () => {
    setShowEdit(false);
    setEditItem(null);
  };

  // Save handler for edits
  const handleEditSave = (updated: T) => {
    if (!editItem) return;
    const key = rowKey(editItem);
    const newItem = { ...editItem, ...updated };
    const newItems = dataSourceItems.map(i => rowKey(i) === key ? newItem : i);
    setItems(newItems);
    setShowEdit(false);
    setEditItem(null);
    if (onChange) onChange(newItems, 'edit', newItem);
  };

  // Helper to create empty data for add form
  const emptyData = formFieldsToUse.reduce((acc, field) => {
    const col = inferredColumns.find(c => c.key === field.key);
    if (field.defaultValue !== undefined) {
      acc[field.key] = field.defaultValue;
    } else if (col && col.defaultValue !== undefined) {
      acc[field.key] = col.defaultValue;
    } else {
      acc[field.key] = field.type === 'checkbox' ? false : field.type === 'number' ? 0 : '';
    }
    return acc;
  }, {} as any);

  // Add handler
  const handleAdd = () => { if (onAddRequest) { onAddRequest(); return; } setShowAdd(true); };
  const handleAddClose = () => setShowAdd(false);
  const handleAddSave = (newItem: T) => {
    // Assign a unique id if possible
    let itemToAdd = newItem;
    if (typeof newItem === 'object' && newItem !== null && 'id' in (newItem as any)) {
      const nextId = dataSourceItems.length > 0 ? Math.max(...dataSourceItems.map(i => (i as any).id || 0)) + 1 : 1;
      itemToAdd = { ...(newItem as any), id: nextId };
    }
    const newItems = [...dataSourceItems, itemToAdd];
    setItems(newItems);
    setShowAdd(false);
    if (onChange) onChange(newItems, 'add', itemToAdd);
  };

  // Delete handler
  const handleDelete = (item: T) => {
    setDeleteItem(item);
    setShowDelete(true);
  };
  const handleDeleteConfirm = () => {
    if (deleteItem) {
      if (onDeleteRequest) {
        // Delegate to the caller — they own the API call and data reload.
        onDeleteRequest(deleteItem);
      } else {
        // Built-in local-state-only removal (no API).
        const newItems = getItems().filter(i => rowKey(i) !== rowKey(deleteItem));
        setItems(newItems);
        if (onChange) onChange(newItems, 'delete', deleteItem);
      }
    }
    setShowDelete(false);
    setDeleteItem(null);
  };
  const handleDeleteCancel = () => {
    setShowDelete(false);
    setDeleteItem(null);
  };

  // Add state and handlers for resizing
  const [flyinWidth, setFlyinWidth] = useState<string | number>('480px');
  const resizing = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(480);

  function startResize(e: React.MouseEvent) {
    resizing.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = typeof flyinWidth === 'number' ? flyinWidth : parseInt(flyinWidth as string, 10) || 480;
    document.addEventListener('mousemove', doResize);
    document.addEventListener('mouseup', stopResize);
  }
  function doResize(e: MouseEvent) {
    if (!resizing.current) return;
    const delta = startXRef.current - e.clientX;
    let newWidth = startWidthRef.current + delta;
    if (newWidth < 320) newWidth = 320;
    if (newWidth > window.innerWidth) newWidth = window.innerWidth;
    setFlyinWidth(newWidth); 
  }
  function stopResize() {
    resizing.current = false;
    document.removeEventListener('mousemove', doResize);
    document.removeEventListener('mouseup', stopResize);
  }

  // Store refs for filter icons per column
  const filterIconRefs = useRef<{ [key: string]: React.RefObject<HTMLSpanElement> }>({});
  inferredColumns.forEach(col => {
    if (!filterIconRefs.current[col.key]) {
      filterIconRefs.current[col.key] = React.createRef() as React.RefObject<HTMLSpanElement>;
    }
  });

  // Helper to format a raw date/ISO string as dd-MM-yyyy for display
  // Uses local time methods so UTC-stored dates (e.g. "2025-12-31T23:00:00Z") render
  // as the correct local date (e.g. 01-01-2026 for UTC+1 browsers).
  const formatDateDisplay = (value: any): string => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    const day   = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year  = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Normalize date fields to yyyy-MM-dd in local time before passing to the edit form,
  // so the DateInput widget receives the correct local date rather than the UTC date portion.
  const normalizeEditItem = (item: T): T => {
    const result: any = { ...(item as any) };
    inferredColumns.forEach(col => {
      if (col.type === 'date') {
        const field = col.dataField ?? col.key;
        if (result[field] && typeof result[field] === 'string') {
          const date = new Date(result[field]);
          if (!isNaN(date.getTime())) {
            const year  = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day   = String(date.getDate()).padStart(2, '0');
            result[field] = `${year}-${month}-${day}`;
          }
        }
      }
    });
    return result as T;
  };

  // ── Toolbar button list ──────────────────────────────────────────────────────
  const builtinToolbarButtons: ToolbarButtonConfig[] = [];
  if (!disableAdd) {
    builtinToolbarButtons.push({
      key: '__add',
      icon: <FaPlus />,
      label: t('add'),
      onClick: handleAdd,
      title: t('add'),
      variant: 'primary',
      role: addRole,
    });
  }
  const allToolbarButtons = [...builtinToolbarButtons, ...toolbarButtons];

  return (
    <div className={`detailed-list ${className || ''}`}>
      {/* Toolbar — always above the table so it's reachable without scrolling */}
      {(allToolbarButtons.length > 0 || toolbarFilters.length > 0) && (
        <Toolbar buttons={allToolbarButtons} filters={toolbarFilters} className="detailed-list__toolbar" />
      )}
      <div className="tableContainer" >
        <table className="table table-bordered table-hover">
          <thead className="table-light">
            <tr>
              {inferredColumns.map(col => {
                const filterIconRef = filterIconRefs.current[col.key];
                const filterOpen = filterAnchor && filterAnchor.key === col.key;
                return (
                  <th
                    key={col.key}
                    style={{ width: colWidths[col.key] || col.width, textAlign: col.align || 'left', cursor: 'pointer', position: 'relative' }}
                    className={col.className + (col.fixedWidth ? ' fixed-width' : '')}
                    onClick={() => handleSort(col)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: col.align || 'left', position: 'relative' }}>
                      <span style={{ flex: 1 }}>{col.header}</span>
                      {sortKey === col.key && sortDir === 'asc' && <span> ▲</span>}
                      {sortKey === col.key && sortDir === 'desc' && <span> ▼</span>}
                      {col.type === 'string' && (
                        <span
                          ref={filterIconRef}
                          style={{ marginLeft: 6, cursor: 'pointer', color: filterValues[col.key] ? '#3572a5' : '#bbb', zIndex: 21 }}
                          onClick={e => {
                            e.stopPropagation();
                            if (filterOpen) {
                              setFilterAnchor(null);
                            } else {
                              setFilterAnchor({ key: col.key, ref: filterIconRef });
                            }
                          }}
                          title={t('filter')}
                        >
                          <FaSearch />
                        </span>
                      )}
                      {!col.fixedWidth && (
                        <span
                          className="resize-handle"
                          onMouseDown={e => { e.stopPropagation(); handleResizeStart(e, col.key); }}
                          style={{ cursor: 'col-resize', padding: '0 4px', userSelect: 'none' }}
                        >
                          ||
                        </span>
                      )}
                    </div>
                    {filterOpen && filterAnchor.ref.current && (
                      <ColumnFilterPopup
                        value={filterValues[col.key] || ''}
                        onChange={val => setFilterValues(f => ({ ...f, [col.key]: val }))}
                        onClose={() => setFilterAnchor(null)}
                        anchorRef={filterIconRef}
                      />
                    )}
                  </th>
                );
              })}
              <th
                style={{ width: actionsColumnWidth || 120, textAlign: 'center', position: 'relative' }}
                className={actionsColumnFixed ? 'fixed-width' : ''}
              >
                {t('actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(item => (
              <tr key={rowKey(item)}
                  onDoubleClick={() => !disableEdit && handleEdit(item)}
              >
                {inferredColumns.map(col => {
                  const value = col.dataField ? (item as any)[col.dataField] : (item as any)[col.key];
                  return (
                    <td key={col.key} style={{ textAlign: col.align || 'left', width: colWidths[col.key] || col.width, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} className={col.className}>
                      {col.render ? col.render(item) : col.type === 'date' ? formatDateDisplay(value) : truncate(value, 40, colWidths[col.key] ? Number(colWidths[col.key]) : undefined)}
                    </td>
                  );
                })}
                <td>
                  {/* Only render additional actions if provided, and render them first */}
                  {actions && actions(item)}
                  {!disableEdit && (
                    <RoleGuard roles={editRole}>
                      <button className="dl-action-btn dl-action-btn--edit" onClick={() => handleEdit(item)} title={t('edit')}>
                        <FaEdit />
                      </button>
                    </RoleGuard>
                  )}
                  {!disableDelete && (
                    <RoleGuard roles={deleteRole}>
                      <button className="dl-action-btn dl-action-btn--delete" onClick={() => handleDelete(item)} title={t('delete')}>
                        <FaTrash />
                      </button>
                    </RoleGuard>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Edit Flyout */}
      {showEdit && editItem && (
        <GenericFlyoutPanel open={showEdit} onClose={handleEditClose} title={t('editRow')}>
          <FormGenerator
            fields={formFieldsToUse}
            initialData={normalizeEditItem(editItem)}
            onSubmit={handleEditSave}
            submitLabel={t('save')}
          />
        </GenericFlyoutPanel>
      )}
      {/* Add Flyout */}
      {showAdd && (
        <GenericFlyoutPanel open={showAdd} onClose={handleAddClose} title={t('addNewRecord')}>
          <FormGenerator
            fields={formFieldsToUse}
            initialData={emptyData}
            onSubmit={handleAddSave}
            submitLabel={t('add')}
          />
        </GenericFlyoutPanel>
      )}
      {/* Delete Confirmation Dialog */}
      {showDelete && deleteItem && (
        <PopupDialog
          open={showDelete}
          title={t('confirmDeletion')}
          onClose={handleDeleteCancel}
          actions={
            <>
              <button className="btn btn-danger me-2" onClick={handleDeleteConfirm}>
                <FaTrash /> {t('delete')}
              </button>
              <button className="btn btn-secondary" onClick={handleDeleteCancel}>{t('cancel')}</button>
            </>
          }
        >
          <p>{t('confirmDeleteMessage')}</p>
        </PopupDialog>
      )}
    </div>
  );
}

export default DetailedListComponent;
